<template>
  <form class="filter">
    <CaretButton
      :class="buttonClasses"
      :aria-expanded="expanded"
      @click.native.prevent="toggleExpanded"
      data-cy="toggle-filter"
    >
      <span v-if="numActiveFilters.value > 0" class="filter__active-marker">
        <span class="visually-hidden">
          {{ t('cubeFilter.activeFilters') }}
        </span>
        {{ numActiveFilters }}
      </span>
      {{ t('cubeFilter.filter') }}
    </CaretButton>
    <transition name="expand">
      <div class="filter__body" v-if="expanded">
        <div class="filter__options stack">
          <fieldset class="stack">
            <div class="filter-toggle stack-horizontal">
              <div class="filter-toggle__input">
                <Toggle
                  v-model="showOnlyDonationRecipients"
                  :color="primaryColor"
                  aria-labelledby="donation-recipients-label"
                ></Toggle>
              </div>
              <span
                class="filter-toggle__label"
                id="donation-recipients-label"
                @click="toggleShowDonationRecipients"
              >
                {{ t('cubeFilter.donationRecipients') }}
              </span>
            </div>
            <div class="filter-toggle stack-horizontal">
              <div class="filter-toggle__input">
                <Toggle
                  v-model="showOnlyMyFriends"
                  :color="primaryColor"
                  aria-labelledby="my-friends-label"
                ></Toggle>
              </div>
              <span
                class="filter-toggle__label"
                id="my-friends-label"
                @click="toggleShowOnlyMyFriends"
              >
                {{ t('cubeFilter.onlyMyFriends') }}
              </span>
            </div>
            <div class="filter-toggle stack-horizontal">
              <div class="filter-toggle__input">
                <Toggle
                  v-model="showOnlyProducers"
                  :color="primaryColor"
                  aria-labelledby="producer-label"
                  data-cy="filter-show-only-producers"
                ></Toggle>
              </div>
              <span
                class="filter-toggle__label"
                id="producer-label"
                @click="toggleShowOnlyProducers"
              >
                {{ t('cubeFilter.onlyProducers') }}
              </span>
            </div>
            <div
              class="filter-toggle stack-horizontal"
              v-if="showOnlyProducers.value"
            >
              <div class="filter-toggle__input">
                <Toggle
                  v-model="showOnlyProducersWithCapacity"
                  :color="primaryColor"
                  aria-labelledby="producer-capacity-label"
                  ></Toggle>
              </div>
              <span
                class="filter-toggle__label"
                id="producer-capacity-label"
                @click="toggleShowOnlyProducersWithCapacity"
              >
                {{ t('cubeFilter.onlyProducersWithCapacity') }}
              </span>
            </div>

            <div
              class="filter-toggle stack-horizontal"
              v-if="isGrantedGridFeeDiscount"
            >
              <div class="filter-toggle__input">
                <Toggle
                  v-model="showOnlyProducersWithGridFeeDiscount"
                  :color="primaryColor"
                  aria-labelledby="producer-grid-fee-discount-label"
                ></Toggle>
              </div>
              <span
                class="filter-toggle__label"
                id="producer-grid-fee-discount-label"
                @click="toggleShowOnlyProducersWithGridFeeDiscount"
              >
                {{ t('cubeFilter.onlyProducersWithGridFeeDiscount') }}
              </span>
            </div>
          </fieldset>
          <fieldset v-if="showOnlyProducers.value" class="filter--energy-type">
            <label id="energy-type-label" class="filter__label">
              {{ t('cubeFilter.energyType') }}
            </label>
            <StyledSelect label="energy-type-label" v-model="energyType">
              <option value="all">
                {{ t('cubeFilter.allEnergyTypes') }}
              </option>
              <option
                v-for="(name, value) in productionTypes"
                :key="value"
                :value="value"
              >
                {{ name }}
              </option>
            </StyledSelect>
          </fieldset>
          <fieldset v-if="showOnlyProducers.value">
            <legend id="price-label" class="filter__label">
              {{ t('cubeFilter.price') }}
            </legend>
            <div class="stack-horizontal">
              <label class="filter-range__label" for="price-min">
                <span>
                  {{ t('cubeFilter.from') }}
                </span>
                <span>
                  {{ minPrice }}
                  <span class="filter__unit">
                    {{ t('cubeFilter.priceUnit') }}
                  </span>
                </span>
              </label>
              <input
                class="filter-range"
                type="range"
                min="0"
                max="36"
                v-model="minPrice"
                id="price-min"
              />
            </div>
            <div class="stack-horizontal">
              <label class="filter-range__label" for="price-max">
                <span>
                  {{ t('cubeFilter.to') }}
                </span>
                <span>
                  {{ maxPrice }}
                  <span class="filter__unit">
                    {{ t('cubeFilter.priceUnit') }}
                  </span>
                </span>
              </label>
              <input
                class="filter-range"
                type="range"
                min="0"
                max="36"
                v-model="maxPrice"
                id="price-max"
              />
            </div>
          </fieldset>
          <fieldset class="filter--name">
            <label class="filter__label" for="search">{{ t('cubeFilter.search') }}</label>
            <input
              v-model="searchTerm"
              id="search"
              class="filter__text-input"
              :placeholder="t('cubeFilter.searchPlaceholder')"
            />
          </fieldset>
          <div class="filter__actions stack-horizontal">
            <button
              class="button button--link"
              @click.prevent="resetFilter"
              data-cy="filter-reset"
            >
              {{ t('cubeFilter.reset') }}
            </button>
            <div v-if="numResults > 0" class="filter__results" data-cy="filter-results">
              {{ numResults }} {{ t('cubeFilter.results') }}
            </div>
          </div>
        </div>
      </div>
    </transition>
  </form>
</template>

<script setup>
  import { toRef, watch, computed } from 'vue'
  import CaretButton from '@/components/efriends/CaretButton.vue'
  import StyledSelect from '@/components/StyledSelect.vue'
  import { useToggle } from '@/composables/useToggle.ts'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useCubeFilter } from '@/composables/useCubeFilter.ts'
  import Toggle from '@vueform/toggle'
  import { useColors } from '@/composables/useColors.ts'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { useMapStore } from '@/stores/useMapStore'
  import { storeToRefs } from 'pinia'
  import strings from '@/util/Strings'

  const props = defineProps({
    numResults: {
      type: Number,
      default: 0,
    },
    buttonClasses: {
      type: String,
      default: 'button button--secondary',
    },
    expandedByDefault: {
      type: Boolean,
      default: false,
    },
  })

  const { t } = useStrings()
  const cubeFilter = useCubeFilter()
  const { colorPrimary: primaryColor } = useColors()
  const numResults = toRef(() => props.numResults)
  const [expanded, toggleExpanded] = useToggle(props.expandedByDefault)
  const { isGrantedGridFeeDiscount } = storeToRefs(useCubeStore())

  const emit = defineEmits(['filterChanged'])
  const emitOnFilterChanged = () => {
    emit('filterChanged')
  }

  const numActiveFilters = computed(() => cubeFilter.numActiveFilters)

  const showOnlyProducers = computed({
    get: () => cubeFilter.showOnlyProducers,
    set: () => {
      cubeFilter.toggleShowOnlyProducers()
      emitOnFilterChanged()
    },
  })

  const showOnlyProducersWithCapacity = computed({
    get: () => cubeFilter.showOnlyProducersWithCapacity,
    set: () => {
      cubeFilter.toggleShowOnlyProducersWithCapacity()
      emitOnFilterChanged()
    },
  })

  const showOnlyProducersWithGridFeeDiscount = computed({
    get: () => cubeFilter.showOnlyProducersWithGridFeeDiscount,
    set: () => {
      cubeFilter.toggleShowOnlyProducersWithGridFeeDiscount()
      emitOnFilterChanged()
    },
  })

  const showOnlyMyFriends = computed({
    get: () => cubeFilter.showOnlyMyFriends,
    set: () => {
      cubeFilter.toggleShowOnlyMyFriends()
      emitOnFilterChanged()
    },
  })

  const showOnlyDonationRecipients = computed({
    get: () => cubeFilter.showOnlyDonationRecipients,
    set: () => {
      cubeFilter.toggleShowOnlyDonationRecipients()
      emitOnFilterChanged()
    },
  })

  const toggleShowOnlyProducers = () => {
    cubeFilter.toggleShowOnlyProducers(),
    emitOnFilterChanged()
  }

  const toggleShowOnlyProducersWithCapacity = () => {
    cubeFilter.toggleShowOnlyProducersWithCapacity()
    emitOnFilterChanged()
  }

  const toggleShowOnlyProducersWithGridFeeDiscount = () => {
    cubeFilter.toggleShowOnlyProducersWithGridFeeDiscount()
    emitOnFilterChanged()
  }

  const toggleShowOnlyMyFriends = () => {
    cubeFilter.toggleShowOnlyMyFriends()
    emitOnFilterChanged()
  }

  const toggleShowDonationRecipients = () => {
    cubeFilter.toggleShowOnlyDonationRecipients()
    emitOnFilterChanged()
  }

  const energyType = computed({
    get: () => cubeFilter.energyType.value,
    set: (type) => {
      cubeFilter.setEnergyType(type)
      emitOnFilterChanged()
    },
  })

  const minPrice = computed({
    get: () => cubeFilter.minPrice.value,
    set: (price) => {
      cubeFilter.setMinPrice(price)
      emitOnFilterChanged()
    },
  })

  const maxPrice = computed({
    get: () => cubeFilter.maxPrice.value,
    set: (price) => {
      cubeFilter.setMaxPrice(price)
      emitOnFilterChanged()
    },
  })

  const searchTerm = computed({
    get: () => cubeFilter.searchTerm.value,
    set: (term) => {
      cubeFilter.setSearchTerm(term)
      emitOnFilterChanged()
    },
  })

  const resetFilter = () => {
    cubeFilter.resetFilters()
    emitOnFilterChanged()
  }

  const productionTypes = computed(() => {
    const { globalCubeList } = storeToRefs(useMapStore())
    const presentTypes = new Set(
      globalCubeList.value
        .filter((cube) => cube.hasProduction && cube.productionType && cube.productionType !== 'none')
        .map((cube) => cube.productionType)
    )
    return [...presentTypes].reduce((obj, key) => {
      obj[key] = strings.productionTypes[key] || key
      return obj
    }, {})
  })

  // Ensure price values never overlap and create a negative range
  watch(minPrice, (newValue) => {
    maxPrice.value = Math.max(newValue, maxPrice.value)
  })

  watch(maxPrice, (newValue) => {
    minPrice.value = Math.min(newValue, minPrice.value)
  })
</script>

<style lang="scss">

  .stack {
    display: flex;
    flex-direction: column;
  }

  .stack-horizontal {
    display: flex;
    align-items: center;
  }

  .filter {
    display: flex;
    flex-direction: column;
    overflow: visible;

    &--energy-type {
      margin-top: $spacer10;
    }

    &--name {
      margin-top: $spacer40;
    }

    &-toggle {
      margin-top: $spacer05;

      &:first-of-type {
        margin-top: 0;
      }

      &__input {
        margin-right: $spacer10;
      }

      &__label {
        cursor: pointer;
      }
    }

    &__actions {
      margin-top: $spacer20;
      justify-content: space-between;

      > button:first-child {
        padding-left: 0;
      }
    }
  }

  .filter__active-marker {
    display: inline-block;
    width: 1.5em;
    height: 1.5em;
    margin-right: 0.2em;
    background-color: $grey-darker;
    border-radius: 50%;
    margin-right: 0.2em;
    font-size: 0.9em;
    color: $white;
  }

  .filter__body {
    position: relative;
    max-height: 38rem;
    width: 100%;
    background-color: ghostwhite;
    overflow: hidden;
    z-index: 1;
  }

  .expand-enter-active,
  .expand-leave-active {
    transition: max-height 0.3s ease-out;
  }
  .expand-enter,
  .expand-leave-to {
    max-height: 0;
  }

  .filter__options {
    padding: $spacer30 $spacer10 $spacer20;
  }

  .filter__label {
    display: block;
    margin-bottom: 0;
    font-size: 0.9em;
    color: $grey-dark;
  }

  .filter-range {
    margin-top: 0;
    margin-bottom: 0;
    margin-left: $spacer10;
  }

  .filter-range__label {
    display: flex;
    min-width: 11ch;
    justify-content: space-between;
    white-space: nowrap;
  }

  .filter__unit {
    font-size: 0.8em;
    align-self: flex-end;
    color: $grey-dark;
  }

  .filter__text-input {
    width: 100%;
    height: 2.5rem;
    appearance: none;
    border: 0;
    color: inherit;
    border-bottom: 2px solid $grey-dark;
    background-color: transparent;
    padding: 0;
  }



  .filter__results {
    white-space: nowrap;
  }
</style>
